import React from 'react';
import logo from 'assets/images/logo-2-color.svg';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FormikHelpers, useFormik } from 'formik';
import { object, ref, string } from 'yup';
import REGULARS from 'components/UI_Kit/utils/regulars';
import { authService } from 'services/api/auth';
import useAsyncFetch from 'hooks/async-fetch';
import FormText from 'components/UI_kit_3/FormText/FormText';
import ValidationCheck from 'components/UI_kit_3/ValidationCheck/ValidationCheck';
import classNames from 'classnames';
import Button from 'components/UI_kit_3/Button/Button';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import { ReactComponent as ArrowLeft } from 'assets/icons_3/arrows/arrow-left.svg';
import { ReactComponent as LockIcon } from 'assets/icons_3/lock.svg';
import { useUserThunkDispatch } from 'hooks';
import { ILoginBody } from 'store/user/types/api-bodies.types';
import { ILoginForm } from 'store/user/types/forms.types';
import { userActions } from 'store/user';
import i18n from 'services/i18n';
import styles from './password-set-page.module.scss';

interface PasswordForm {
  password: string;
  passwordConfirm: string;
}

type PasswordSaveParams = [string, FormikHelpers<PasswordForm>];

const PasswordSetPage = () => {
  const [t] = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  const thunkUserDispatch = useUserThunkDispatch();
  const navigate = useNavigate();

  const [handleLogin, loginLoading] = useAsyncFetch({
    asyncFunc: async (form: ILoginForm) => {
      const body: ILoginBody = {
        login: form.email,
        password: form.password,
      };
      const res = await thunkUserDispatch(userActions.signIn(body, i18n.language));
      navigate('/', { replace: true });
      return res;
    },
    errorNote: true,
    disableRedirectOn401: true,
  });

  const [savePassword, saveLoading, success] = useAsyncFetch<PasswordSaveParams, boolean>({
    async asyncFunc(password) {
      const { data } = await authService.activateSelfRegistrationUser(password, token || '');
      await handleLogin({ email: data.data.login, password });
      return true;
    },
    errorHandler: ({ errMessage, params: [, helpers] }) => {
      helpers.setFieldError('passwordConfirm', errMessage);
    },
    initialState: false,
  });

  const loading = saveLoading || loginLoading;

  const schema = React.useMemo(() => object().shape({
    password: string()
      .required(t('form.error.required'))
      .min(12, t('form.error.password_weak'))
      .matches(REGULARS.password, t('form.error.password_weak')),
    passwordConfirm: string().required(t('form.error.required')).oneOf([ref('password'), null], t('form.error.confirm')),
  }), [t]);

  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
  } = useFormik({
    validationSchema: schema,
    onSubmit: (form, helpers) => savePassword(form.password, helpers),
    initialValues: {
      password: '',
      passwordConfirm: '',
    },
    validateOnChange: false,
  });

  const passwordValidation = {
    minCharacters: values.password.length >= 12,
    numbers: /\d/.test(values.password),
    capitals: /[A-Z]/.test(values.password),
    specSymbols: /[!@#$%^&*]/.test(values.password),

  };

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className={styles.passwordResetPage}>
      <div className={styles.logo}>
        <img src={logo} width={139} height={32} alt="pro-data" />
      </div>
      <div className={classNames(styles.lockIcon)}>
        <LockIcon />
      </div>
      <form className={styles.form} noValidate onSubmit={handleSubmit} id="account-form" autoComplete="off">
        <h1 className="font-2xl-semibold mb-4xl">{t('auth.password_creation')}</h1>
        {success && <p>{t('auth.password_changed')}</p>}
        {!success && (
          <>
            <FormText
              className="mb-2xl"
              id="password"
              type="password"
              name="password"
              label={t('user.new_password')}
              autoComplete="new-password"
              value={values?.password}
              error={errors.password}
              isInvalid={touched.password && !!errors.password}
              onChange={handleChange}
            />
            <div className={classNames(styles.validation, 'mb-2xl')}>
              <ValidationCheck label={t('auth.password_requirements_min_12')} checked={passwordValidation.minCharacters} />
              <ValidationCheck label={t('auth.password_requirements_2')} checked={passwordValidation.numbers} />
              <ValidationCheck label={t('auth.password_requirements_3')} checked={passwordValidation.capitals} />
              <ValidationCheck label={t('auth.password_requirements_4')} checked={passwordValidation.specSymbols} />
            </div>
            <FormText
              className="mb-3xl"
              id="passwordConfirm"
              type="password"
              name="passwordConfirm"
              autoComplete="new-password"
              label={t('form.field.confirm_password')}
              value={values?.passwordConfirm}
              onChange={handleChange}
              error={errors.passwordConfirm}
              isInvalid={touched.passwordConfirm && !!errors.passwordConfirm}
            />
            <Button
              className="mb-4xl w-100"
              disabled={loading}
              type="submit"
              size="lg"
              form="account-form"
              variant="primary"
              qa-element="password-reset-save"
            >
              {t('common.save')}
            </Button>
          </>
        )}
        <RouterLinkButton variant="link-gray" prefixIcon={<ArrowLeft />} className="w-100" to="/" replace qa-element="to-main">{t('common.back_to_main')}</RouterLinkButton>
      </form>
      <div className="mt-auto font-sm-regular text-tertiary">{window.location.host.includes('kz-1') ? t('common.rights_kz') : t('common.rights')}</div>

    </main>
  );
};

export default PasswordSetPage;
